import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { PingOnboardingPanel } from '../components/PingOnboardingPanel';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import { normalizePhoneNumber } from '../utils/phoneNumber';
import { emailAddressProblem, normalizeEmailAddress } from '../utils/emailAddress';
import { CompanyOptionalFields } from '../components/CompanyOptionalFields';
import { BankPicker, useBankOptions } from '../components/BankPicker';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StepContent, FormInput, Button, Card } from '../components';
import { BankIdLayout, RegistrationSteps } from '../components/BankIdLayout';
import { EmailConfirmation } from '../components/EmailConfirmation';
import { useAuth } from '../context/AuthContext';
import { useLocalBankIdDebug } from '../context/LocalBankIdDebugContext';
import { onboardingApi, systemApi, lawyerApi, inspectorApi, orgNumberApi } from '../services/api';
import type { ProfileStepData, CompanyStepData, UserOnboardingState } from '../types';
import { authenticatedHomePath } from '../utils/onboarding';
import { nameMatchesCompany } from '../utils/personCompanyName';
import { isValidVatSE, vatFromOrgNumber } from '../utils/vat';
import { isValidOrgNumber } from '../utils/orgNumber';
import { withTestOrgNumber, companyIdentifierMessages, sanitizeOrgNumberInput, sanitizeVatInput } from '../utils/companyIdentifiers';

const COMPANY_STEPS = [
  { key: 'bankid', label: '' },
  { key: 'profile', label: '' },
  { key: 'company', label: '' },
  { key: 'terms', label: '' },
  { key: 'email', label: '' },
  { key: 'complete', label: '' }
];

const PREPARED_COMPANY_STEPS = [
  { key: 'bankid', label: '' },
  { key: 'profile', label: '' },
  { key: 'terms', label: '' },
  { key: 'email', label: '' },
  { key: 'company', label: '' },
  { key: 'complete', label: '' }
];

const PERSON_STEPS = [
  { key: 'bankid', label: '' },
  { key: 'profile', label: '' },
  { key: 'terms', label: '' },
  { key: 'email', label: '' },
  { key: 'complete', label: '' }
];

const PROVIDER_STEPS = [
  { key: 'bankid', label: '' },
  { key: 'profile', label: '' },
  { key: 'terms', label: '' },
  { key: 'email', label: '' },
  { key: 'complete', label: '' }
];

const digitsOnly = (value: string) => value.replace(/\D/g, '');

type ProviderProfileStepData = {
  companyName: string;
  organizationNumber: string;
  vatNumber: string;
  notVatRegistered: boolean;
};

export function Onboarding() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const debug = useLocalBankIdDebug();
  const testserver = debug.enabled && debug.environment === 'testserver';
  const [currentStepKey, setCurrentStepKey] = useState<UserOnboardingState['currentStep']>('profile');
  const [companyPreparedByAdmin, setCompanyPreparedByAdmin] = useState(false);
  // A Besiktningsman or Jurist invited by Etappio (ClickUp 869fjt7ma): the
  // prepared profile is theirs once the invited e-mail is confirmed, so the
  // business details are not asked for before that.
  const [providerPreparedByAdmin, setProviderPreparedByAdmin] = useState(false);
  const [providerProfilePending, setProviderProfilePending] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isPerson = user?.personaType === 'person';
  const isCompanyAccount = user?.personaType === 'company';
  const isProviderAccount =
    user?.personaType === 'lawyer' || user?.personaType === 'inspector';
  const STEPS = isCompanyAccount
    ? companyPreparedByAdmin ? PREPARED_COMPANY_STEPS : COMPANY_STEPS
    : isProviderAccount
      ? PROVIDER_STEPS
      : PERSON_STEPS;
  const verifiedFullName = user?.onboarding?.verifiedIdentity?.verifiedFullName;
  const profileTitle = isProviderAccount
    ? t('onboarding.providerProfile.title')
    : t('onboarding.profile.title');
  const profileDescription = isProviderAccount
    ? t('onboarding.providerProfile.description')
    : t('onboarding.profile.description');

  const steps = STEPS.map((step) => ({
    ...step,
    label: t(`onboarding.steps.${step.key}`)
  }));

  // Contact details from an invitation (set by Register), read once on mount.
  const [invitationContact] = useState<{ email?: string; phone?: string }>(() => {
    try {
      const contact = JSON.parse(sessionStorage.getItem('onboardingContact') || '{}');
      return contact && typeof contact === 'object' ? contact : {};
    } catch {
      return {};
    }
  });
  const [profileData, setProfileData] = useState<ProfileStepData>({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    postalCode: user?.postalCode || '',
    city: user?.city || '',
    bankIds: user?.banks?.map(bank => bank.id) ?? []
  });
  // A Privatperson chooses at least one bank (ClickUp 869eu6fpm).
  const bankOptions = useBankOptions(isPerson);
  const [banksTouched, setBanksTouched] = useState(false);

  const [companyData, setCompanyData] = useState<CompanyStepData>({
    companyName: '',
    organizationNumber: '',
    vatNumber: '',
    address: '',
    city: '',
    postalCode: '',
    email: user?.email || ''
  });
  const [companyLoaded, setCompanyLoaded] = useState(false);
  const [companyLoadError, setCompanyLoadError] = useState(false);
  const [invitationEmailMismatch, setInvitationEmailMismatch] = useState(false);
  const companyEdited = useRef(false);
  const preparedCompanyOrg = useRef('');
  const [providerData, setProviderData] = useState<ProviderProfileStepData>({
    companyName: '',
    organizationNumber: '',
    vatNumber: '',
    notVatRegistered: false
  });

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [agreementContent, setAgreementContent] = useState('');
  const [agreementLoading, setAgreementLoading] = useState(true);
  const [agreementError, setAgreementError] = useState(false);
  const [agreementMeta, setAgreementMeta] = useState<{
    version: string | null;
    savedAt: string | null;
    agreementType: string;
    title: string | null;
  }>({ version: null, savedAt: null, agreementType: '', title: null });
  // Bumped to reload the terms, e.g. when a newer version was published while reading.
  const [termsReload, setTermsReload] = useState(0);
  const [termsVersionChanged, setTermsVersionChanged] = useState(false);
  // Gate the accept checkbox until the user has scrolled to the end of the terms.
  const [termsScrolledToEnd, setTermsScrolledToEnd] = useState(false);
  const termsScrollRef = useRef<HTMLDivElement | null>(null);

  // All user groups accept the same current Användarvillkor version.
  useEffect(() => {
    let cancelled = false;
    setAgreementLoading(true);
    setAgreementError(false);
    systemApi.getTermsOfService()
      .then((data) => {
        if (cancelled) return;
        if (!data.content?.trim() || !data.version || !data.savedAt) {
          throw new Error('Current terms are incomplete');
        }
        setAgreementContent(data.content);
        setAgreementMeta({
          version: data.version,
          savedAt: data.savedAt,
          agreementType: data.agreementType,
          title: data.termsTitle ?? null
        });
      })
      .catch(() => {
        if (cancelled) return;
        setAgreementContent('');
        setAgreementMeta({ version: null, savedAt: null, agreementType: '', title: null });
        setAgreementError(true);
      })
      .finally(() => {
        if (!cancelled) setAgreementLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [termsReload]);

  const evaluateTermsScroll = () => {
    const el = termsScrollRef.current;
    if (!el) return;
    // Reached the bottom, or the content fits without scrolling (edge case).
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 8) {
      setTermsScrolledToEnd(true);
    }
  };

  // Apply only editable fields, and never overwrite edits with a late response.
  useEffect(() => {
    let cancelled = false;
    setCompanyLoadError(false);
    setCompanyLoaded(false);
    onboardingApi.getStatus().then(status => {
      if (cancelled) return;
      setCompanyPreparedByAdmin(status.companyPreparedByAdmin === true);
      setProviderPreparedByAdmin(status.providerPreparedByAdmin === true);
      setProviderProfilePending(status.providerProfilePending === true);
      setInvitationEmailMismatch(status.invitationEmailMismatch === true);
      if (!companyEdited.current) {
        const profile = status.companyProfile;
        if (profile) {
          preparedCompanyOrg.current = status.companyPreparedByAdmin ? digitsOnly(profile.organizationNumber || '') : '';
          setCompanyData({
            companyName: profile.companyName || '', organizationNumber: profile.organizationNumber || '',
            vatNumber: profile.vatNumber || '', address: profile.address || '', city: profile.city || '',
            postalCode: digitsOnly(profile.postalCode || ''), email: profile.email || '', phone: profile.phone || '',
            website: profile.website || '', country: profile.country || '', companyPresentation: profile.companyPresentation || '',
            specialties: profile.specialties || [], languages: profile.languages || [], geographicArea: profile.geographicArea || '',
            hourlyRate: profile.hourlyRate || '', responseTime: profile.responseTime || '', leadTime: profile.leadTime || '',
            experienceYears: profile.experienceYears || 0
          });
        } else if (status.invitationCompanyName) {
          setCompanyData(previous => ({ ...previous, companyName: status.invitationCompanyName || '' }));
        }
      }
      setCompanyLoaded(true);
    }).catch(() => { if (!cancelled) setCompanyLoadError(true); });
    return () => { cancelled = true; };
  }, [user?.emailVerified, user?.email]);

  useEffect(() => {
    if (!user) return;
    setProfileData(previous => ({
      fullName: user.onboarding?.verifiedIdentity?.verifiedFullName || user.fullName || '',
      // A later user refresh must not drop the contact details from the invitation.
      email: user.email || invitationContact.email || '',
      phone: user.phone || invitationContact.phone || '',
      address: user.address || '',
      postalCode: digitsOnly(user.postalCode || ''),
      city: user.city || '',
      // Nor the banks the person has just chosen (ClickUp 869eu6fpm).
      bankIds: previous.bankIds?.length ? previous.bankIds : user.banks?.map(bank => bank.id) ?? []
    }));
  }, [user, invitationContact]);

  useEffect(() => {
    if (!user || !isProviderAccount) return;
    const loadProviderProfile = async () => {
      const provider = await (user.personaType === 'lawyer'
        ? lawyerApi.getLawyer()
        : inspectorApi.getInspector());
      const providerCompanyNames = provider as {
        companyName?: string | null;
        lawyerCompanyName?: string | null;
        inspectorCompanyName?: string | null;
      };
      const companyName =
        user.personaType === 'lawyer'
          ? providerCompanyNames.lawyerCompanyName || providerCompanyNames.companyName || ''
          : providerCompanyNames.inspectorCompanyName || providerCompanyNames.companyName || '';
      setProviderData({
        companyName,
        organizationNumber: provider.organizationNumber || '',
        vatNumber: provider.vatNumber || '',
        notVatRegistered: Boolean(provider.notVatRegistered)
      });
      setProfileData((prev) => ({
        ...prev,
        fullName: prev.fullName,
        phone: provider.phone || prev.phone || '',
        address: provider.address || prev.address || '',
        postalCode: digitsOnly(provider.postalCode || prev.postalCode || ''),
        city: provider.city || prev.city || ''
      }));
    };
    loadProviderProfile().catch(() => {});
  }, [isProviderAccount, user]);

  useEffect(() => {
    setProfileData(previous => ({ ...previous, email: previous.email || invitationContact.email || '', phone: previous.phone || invitationContact.phone || '' }));
    try { sessionStorage.removeItem('onboardingContact'); } catch { /* Storage blocked. */ }
  }, [invitationContact]);

  // A pasted or finished address is shown trimmed and in lower case, so the
  // person sees exactly what is saved. Characters are never replaced.
  const emailPasted = useRef(false);
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const pasted = name === 'email' && emailPasted.current;
    emailPasted.current = false;
    const nextValue =
      name === 'postalCode' ? digitsOnly(value) : pasted ? normalizeEmailAddress(value) : value;
    setProfileData((prev) => ({ ...prev, [name]: nextValue }));
  };
  const normalizeProfileEmail = () =>
    setProfileData((prev) => (prev.email ? { ...prev, email: normalizeEmailAddress(prev.email) } : prev));
  const profileEmailProblem = profileData.email ? emailAddressProblem(profileData.email) : null;

  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    companyEdited.current = true;
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'postalCode') nextValue = digitsOnly(value);
    if (name === 'organizationNumber') {
      nextValue = sanitizeOrgNumberInput(value);
      if (companyOrgTaken) setCompanyOrgTaken(false);
    }
    if (name === 'vatNumber') nextValue = sanitizeVatInput(value);
    setCompanyData((prev) => {
      const next = { ...prev, [name]: nextValue };
      // Förifyll moms-nr från orgnr så länge momsfältet är orört/tomt (redigerbart).
      if (name === 'organizationNumber' && !prev.vatNumber) {
        const derived = vatFromOrgNumber(nextValue);
        if (derived) next.vatNumber = derived;
      }
      return next;
    });
  };

  const handleProviderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'organizationNumber') {
      nextValue = sanitizeOrgNumberInput(value);
      if (providerOrgTaken) setProviderOrgTaken(false);
    }
    if (name === 'vatNumber') nextValue = sanitizeVatInput(value);
    setProviderData((prev) => {
      const next = { ...prev, [name]: nextValue };
      if (
        name === 'organizationNumber' &&
        !prev.vatNumber &&
        !prev.notVatRegistered
      ) {
        const derived = vatFromOrgNumber(nextValue);
        if (derived) next.vatNumber = derived;
      }
      return next;
    });
  };

  const handleProviderVatExemptToggle = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const notVatRegistered = e.target.checked;
    setProviderData((prev) => ({
      ...prev,
      notVatRegistered,
      vatNumber: notVatRegistered
        ? ''
        : prev.vatNumber || vatFromOrgNumber(prev.organizationNumber)
    }));
  };

  const [companyOrgTaken, setCompanyOrgTaken] = useState(false);
  const [providerOrgTaken, setProviderOrgTaken] = useState(false);

  // Best-effort blur check; onboardingApi.saveCompany / provider update still
  // enforce uniqueness server-side, so a failed check never blocks the user.
  const checkOrgAvailability = async (
    value: string,
    setTaken: (taken: boolean) => void
  ) => {
    if (!isValidOrgNumber(value)) return;
    if (companyPreparedByAdmin && digitsOnly(value) === preparedCompanyOrg.current) { setTaken(false); return; }
    try {
      const { available } = await orgNumberApi.checkAvailability(value);
      setTaken(!available);
    } catch {
      /* ignore network errors on blur */
    }
  };

  const {
    organizationNumberError: orgNumberError,
    vatNumberError: companyVatError,
    vatNumberWarning: companyVatWarning
  } = companyIdentifierMessages(t, {
    organizationNumber: companyData.organizationNumber,
    vatNumber: companyData.vatNumber,
    organizationNumberTaken: companyOrgTaken
  });
  const {
    organizationNumberError: providerOrgNumberError,
    vatNumberError: providerVatError,
    vatNumberWarning: providerVatWarning
  } = companyIdentifierMessages(t, {
    organizationNumber: providerData.organizationNumber,
    vatNumber: providerData.notVatRegistered ? '' : providerData.vatNumber,
    organizationNumberTaken: providerOrgTaken
  });

  const currentStep = STEPS.findIndex(step => step.key === currentStepKey);

  // When the terms step is shown (or its content changes), reset the scroll gate
  // and immediately open it if the text fits without needing to scroll.
  useEffect(() => {
    if (currentStepKey !== 'terms') return;
    setTermsScrolledToEnd(false);
    const check = () => {
      const el = termsScrollRef.current;
      if (el && el.scrollHeight - el.clientHeight < 8) {
        setTermsScrolledToEnd(true);
      }
    };
    const raf = requestAnimationFrame(check);
    window.addEventListener('resize', check);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', check);
    };
  }, [currentStepKey, agreementContent]);

  const resumeStep = useCallback((state?: UserOnboardingState) => {
    if (state?.status === 'verified') setCurrentStepKey('complete');
    else if (state?.required) setCurrentStepKey(state.currentStep === 'complete' ? 'email' : state.currentStep);
  }, []);

  useEffect(() => {
    resumeStep(user?.onboarding);
    // Only server progress changes should replace a step chosen with Previous.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.onboarding?.currentStep, user?.onboarding?.status, resumeStep]);

  const handleNext = async () => {
    setIsLoading(true);
    setSaveError('');
    try {
      switch (currentStepKey) {
        case 'profile':
          await onboardingApi.saveProfile({
            ...profileData,
            email: profileData.email ? normalizeEmailAddress(profileData.email) : profileData.email,
            phone: normalizePhoneNumber(profileData.phone) || profileData.phone,
            bankIds: isPerson ? profileData.bankIds : undefined
          });
          if (isProviderAccount && !providerProfilePending) {
            if (user?.personaType === 'lawyer') {
              await lawyerApi.updateLawyer({
                lawyerCompanyName: providerData.companyName,
                organizationNumber: providerData.organizationNumber,
                vatNumber: providerData.notVatRegistered ? '' : providerData.vatNumber,
                notVatRegistered: providerData.notVatRegistered
              });
            } else if (user?.personaType === 'inspector') {
              await inspectorApi.updateInspector({
                inspectorCompanyName: providerData.companyName,
                organizationNumber: providerData.organizationNumber,
                vatNumber: providerData.notVatRegistered ? '' : providerData.vatNumber,
                notVatRegistered: providerData.notVatRegistered
              });
            }
          }
          break;
        case 'company':
          await onboardingApi.saveCompany({ ...companyData, email: companyData.email || undefined,
            specialties: companyData.specialties?.map(item => item.trim()).filter(Boolean),
            languages: companyData.languages?.map(item => item.trim()).filter(Boolean)
          });
          break;
        case 'terms':
          await onboardingApi.acceptTerms({
            version: agreementMeta.version ?? undefined,
            versionSavedAt: agreementMeta.savedAt ?? undefined,
            agreementType: agreementMeta.agreementType || undefined
          });
          break;

      }
      const refreshed = await refreshUser();
      if (refreshed?.emailVerified && refreshed.onboarding?.currentStep === 'complete') {
        if (refreshed.onboarding.status !== 'verified') await onboardingApi.complete();
        await refreshUser();
        setCurrentStepKey('complete');
      } else if (refreshed?.onboarding) resumeStep(refreshed.onboarding);
      else setCurrentStepKey(STEPS[Math.min(currentStep + 1, steps.length - 1)].key as UserOnboardingState['currentStep']);
    } catch (error) {
      const code = (error as {
        response?: { data?: { error?: { code?: string } } };
      })?.response?.data?.error?.code;
      if (code === 'TERMS_VERSION_MISMATCH') {
        // A newer version was published after the page loaded: show it and ask again.
        setTermsAccepted(false);
        setTermsVersionChanged(true);
        setTermsReload((value) => value + 1);
      }
      if (code === 'ORG_NUMBER_ALREADY_EXISTS') {
        if (currentStepKey === 'company') setCompanyOrgTaken(true);
        else setProviderOrgTaken(true);
      }
      if (code === 'EMAIL_ADDRESS_UNSUPPORTED') {
        // An address saved before the rule existed: correct it on the contact step.
        setCurrentStepKey('profile');
        setSaveError(t('auth.bankid.emailUnsupported', { email: profileData.email || user?.email || '' }));
        return;
      }
      setSaveError(t(code === 'EMAIL_ALREADY_EXISTS' ? 'auth.bankid.emailTaken' : code === 'INVITATION_EMAIL_MISMATCH' ? 'auth.bankid.invitationEmailMismatch' : 'common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevious = () => {
    if (companyPreparedByAdmin && currentStepKey === 'company') { setCurrentStepKey('profile'); return; }
    setCurrentStepKey(STEPS[Math.max(currentStep - 1, 1)].key as UserOnboardingState['currentStep']);
  };

  const emailComplete = useCallback(() => setCurrentStepKey('complete'), []);

  const handleComplete = async () => {
    setIsLoading(true);
    setSaveError('');
    try {
      if (user?.onboarding?.status !== 'verified') await onboardingApi.complete();
      const refreshedUser = await refreshUser();
      const destination = authenticatedHomePath(refreshedUser ?? user);
      const invitationId = sessionStorage.getItem('pendingInvitation');
      if (invitationId && destination !== '/onboarding' && destination !== '/verify-email') {
        sessionStorage.removeItem('pendingInvitation');
        navigate(companyPreparedByAdmin || providerPreparedByAdmin ? destination : `/invitations/${encodeURIComponent(invitationId)}/decision`);
      } else navigate(destination);
    } catch {
      setSaveError(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStepKey) {
      case 'profile':
        return (
          <StepContent
            title={profileTitle}
            description={profileDescription}
          >
            {invitationEmailMismatch && <p className="alert alert-warning" role="alert">{t('auth.bankid.invitationEmailMismatch')}</p>}
            <FormInput
              name="fullName"
              label={t('auth.bankid.verifiedName')}
              value={verifiedFullName || profileData.fullName}
              readOnly
              icon="user"
              required
              helpText={t('auth.bankid.nameLocked')}
            />
            <FormInput name="email" type="email" inputMode="email" autoComplete="email"
              label={t('auth.register.email')} value={profileData.email || ''} onChange={handleProfileChange}
              onPaste={() => { emailPasted.current = true; }} onBlur={normalizeProfileEmail}
              helpText={t(testserver ? 'localBankIdDebug.emailHelp' : 'auth.bankid.emailHelp')} required
              error={profileEmailProblem === 'unsupportedCharacters' ? t('auth.bankid.emailCharacters') :
                profileEmailProblem ? t('auth.bankid.emailInvalid') :
                testserver && profileData.email && !/\.invalid$/i.test(profileData.email.trim()) ? t('localBankIdDebug.emailInvalid') : undefined} />
            <FormInput
              name="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              error={profileData.phone && !normalizePhoneNumber(profileData.phone) ? t('auth.bankid.phoneInvalid') : undefined}
              label={t('onboarding.profile.phone')}
              value={profileData.phone}
              onChange={handleProfileChange}
              icon="phone"
              required
            />
            {(isPerson || isProviderAccount) && (
              <>
                <AddressAutocomplete
                  name="address"
                  disabled={isLoading}
                  onAddressSelect={address => setProfileData(previous => applySelectedAddress(previous, address))}
                  helpText={t('auth.bankid.addressOptional')}
                  label={
                    isProviderAccount
                      ? t('onboarding.providerProfile.address')
                      : t('auth.register.personAddress')
                  }
                  value={profileData.address || ''}
                  onChange={handleProfileChange}
                  icon="map-pin"
                />
                {/* Side by side only when the card has room for it (869fjpwh8). */}
                <div className="bankid-field-pair" data-testid="onboarding-postal-city">
                  <div>
                    <FormInput
                      name="postalCode"
                      label={
                        isProviderAccount
                          ? t('onboarding.providerProfile.postalCode')
                          : t('auth.register.personPostalCode')
                      }
                      value={profileData.postalCode || ''}
                      error={profileData.postalCode && !/^\d{5}$/.test(profileData.postalCode) ? t('auth.bankid.postalCodeInvalid') : undefined}
                      onChange={handleProfileChange}
                      placeholder={t('auth.bankid.postalCodeExample')}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="city"
                      label={
                        isProviderAccount
                          ? t('onboarding.providerProfile.city')
                          : t('auth.register.personCity')
                      }
                      value={profileData.city || ''}
                      onChange={handleProfileChange}
                    />
                  </div>
                </div>
              </>
            )}
            {isPerson && bankOptions.loadError && (
              <p className="alert alert-danger" role="alert">{t('banks.loadError')}</p>
            )}
            {isPerson && bankOptions.banks && (
              <BankPicker
                banks={bankOptions.banks}
                value={profileData.bankIds ?? []}
                current={user?.banks}
                disabled={isLoading}
                showRequired={banksTouched}
                onChange={bankIds => {
                  setBanksTouched(true);
                  setProfileData(previous => ({ ...previous, bankIds }));
                }}
              />
            )}
            {isProviderAccount && providerProfilePending && (
              <p className="alert alert-info mt-4 mb-0" data-testid="provider-profile-prepared">{t('onboarding.providerProfile.preparedByEtappio')}</p>
            )}
            {isProviderAccount && !providerProfilePending && (
              <>
                <hr className="my-4" />
                <h6 className="fw-bold mb-3">{t('onboarding.providerProfile.businessTitle')}</h6>
                <div className="row">
                  <div className="col-md-6">
                    <FormInput
                      name="companyName"
                      label={t('onboarding.providerProfile.companyName')}
                      value={providerData.companyName}
                      onChange={handleProviderChange}
                      icon="briefcase"
                      required
                    />
                    {nameMatchesCompany(profileData.fullName, providerData.companyName) && (
                      <div className="alert alert-warning py-2 px-3 small mt-n2 mb-3" role="status" data-testid="onboarding-provider-company-matches-person">
                <i className="feather-alert-triangle me-1" aria-hidden="true"></i>
                {t('onboarding.company.nameMatchesPerson')}
              </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <FormInput
                      name="organizationNumber"
                      label={t('onboarding.providerProfile.organizationNumber')}
                      value={providerData.organizationNumber}
                      onChange={handleProviderChange}
                      onBlur={() =>
                        checkOrgAvailability(
                          providerData.organizationNumber,
                          setProviderOrgTaken
                        )
                      }
                      icon="hash"
                      placeholder={t('auth.bankid.organizationNumberExample')}
                      error={providerOrgNumberError}
                      required
                    >
                      <TestOrgNumberButton onGenerated={value => { setProviderOrgTaken(false); setProviderData(previous => withTestOrgNumber(previous, value)); }} />
                    </FormInput>
                    {providerOrgTaken && (
                      <div className="alert alert-light border py-2 px-2 mt-1 mb-2" role="alert">
                        <p className="fs-12 mb-1">
                          {t('onboarding.company.organizationNumberTakenHelp')}
                        </p>
                        <a href="mailto:support@etappio.se" className="fw-bold fs-12">
                          <i className="feather-life-buoy me-1"></i>
                          {t('onboarding.company.contactSupport')}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <FormInput
                  name="vatNumber"
                  label={t('onboarding.providerProfile.vatNumber')}
                  value={providerData.vatNumber}
                  onChange={handleProviderChange}
                  icon="file-text"
                  placeholder={t('auth.bankid.vatExample')}
                  error={providerVatError}
                  disabled={providerData.notVatRegistered}
                />
                {providerVatWarning && (
                  <div className="text-warning small mb-2">
                    <i className="feather-alert-triangle me-1"></i>{providerVatWarning}
                  </div>
                )}
                <div className="form-text mb-2">
                  {t('onboarding.company.vatNumberHelp', 'Format: SE följt av 12 siffror, t.ex. SE556677889901.')}
                </div>
                <div className="form-check mb-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="notVatRegistered"
                    checked={providerData.notVatRegistered}
                    onChange={handleProviderVatExemptToggle}
                  />
                  <label className="form-check-label" htmlFor="notVatRegistered">
                    {t('onboarding.providerProfile.notVatRegistered', 'Ej momsregistrerad')}
                  </label>
                </div>
              </>
            )}
          </StepContent>
        );

      case 'company':
        return (
          <StepContent
            title={t('onboarding.company.title')}
            description={t('onboarding.company.description')}
          >
            <p className="text-muted">{t('companySetup.reviewHelp')}</p>
            {companyLoadError && <div className="alert alert-danger" role="alert">{t('companySetup.loadError')}</div>}
            <div className="row">
              <div className="col-md-6">
                <FormInput
                  name="companyName"
                  label={t('onboarding.company.companyName')}
                  value={companyData.companyName}
                  onChange={handleCompanyChange}
                  icon="briefcase"
                  required
                />
                {nameMatchesCompany(profileData.fullName, companyData.companyName) && (
                  <div className="alert alert-warning py-2 px-3 small mt-n2 mb-3" role="status" data-testid="onboarding-company-matches-person">
                <i className="feather-alert-triangle me-1" aria-hidden="true"></i>
                {t('onboarding.company.nameMatchesPerson')}
              </div>
                )}
              </div>
              <div className="col-md-6">
                <FormInput
                  name="organizationNumber"
                  label={t('onboarding.company.organizationNumber')}
                  value={companyData.organizationNumber}
                  onChange={handleCompanyChange}
                  onBlur={() =>
                    checkOrgAvailability(
                      companyData.organizationNumber,
                      setCompanyOrgTaken
                    )
                  }
                  icon="hash"
                  placeholder={t('auth.bankid.organizationNumberExample')}
                  error={orgNumberError}
                  required
                >
                  <TestOrgNumberButton onGenerated={value => { companyEdited.current = true; setCompanyOrgTaken(false); setCompanyData(previous => withTestOrgNumber(previous, value)); }} />
                </FormInput>
                {companyOrgTaken && (
                  <div className="alert alert-light border py-2 px-2 mt-1 mb-2" role="alert">
                    <p className="fs-12 mb-1">
                      {t('onboarding.company.organizationNumberTakenHelp')}
                    </p>
                    <a href="mailto:support@etappio.se" className="fw-bold fs-12">
                      <i className="feather-life-buoy me-1"></i>
                      {t('onboarding.company.contactSupport')}
                    </a>
                  </div>
                )}
              </div>
            </div>
            <FormInput
              name="vatNumber"
              label={t('onboarding.company.vatNumber', 'Momsregistreringsnummer')}
              value={companyData.vatNumber}
              onChange={handleCompanyChange}
              icon="file-text"
              placeholder={t('auth.bankid.vatExample')}
              error={companyVatError}
              required
            />
            {companyVatWarning && (
              <div className="text-warning small mb-2">
                <i className="feather-alert-triangle me-1"></i>{companyVatWarning}
              </div>
            )}
            <div className="form-text mb-4">
              {t('onboarding.company.vatNumberHelp', 'Format: SE följt av 12 siffror, t.ex. SE556677889901.')}
            </div>
            <AddressAutocomplete
              name="address"
              disabled={isLoading}
              onAddressSelect={address => { companyEdited.current = true; setCompanyData(previous => applySelectedAddress(previous, address)); }}
              label={t('onboarding.company.address')}
              value={companyData.address}
              onChange={handleCompanyChange}
              icon="map-pin"
              required
            />
            <div className="row">
              <div className="col-md-6">
                <FormInput
                  name="postalCode"
                  label={t('onboarding.company.postalCode')}
                  value={companyData.postalCode}
                  onChange={handleCompanyChange}
                  placeholder={t('auth.bankid.postalCodeExample')}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={5}
                  required
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  name="city"
                  label={t('onboarding.company.city')}
                  value={companyData.city}
                  onChange={handleCompanyChange}
                  required
                />
              </div>
            </div>
            {/* Contact and practical details belong in onboarding only when Etappio
                prepared them, so the company can verify and correct them. A
                self-registered company skips them (left empty) and completes
                them later under Inställningar → Företagsuppgifter. */}
            {companyPreparedByAdmin && (
              <details className="company-setup mt-3" open data-testid="onboarding-company-optional-details">
                <summary className="fw-semibold py-3">{t('companySetup.optionalDetails')}</summary>
                <p className="text-muted small">{t('onboarding.company.preparedDetailsHelp')}</p>
                <CompanyOptionalFields value={companyData} section="overview" onChange={patch => {
                  companyEdited.current = true; setCompanyData(previous => ({ ...previous, ...patch }));
                }} />
                <CompanyOptionalFields value={companyData} section="practical" onChange={patch => {
                  companyEdited.current = true; setCompanyData(previous => ({ ...previous, ...patch }));
                }} />
              </details>
            )}
          </StepContent>
        );

      case 'terms':
        return (
          <StepContent
            title={t('onboarding.terms.title')}
            description={t('onboarding.terms.description')}
          >
            {termsVersionChanged && (
              <div className="alert alert-warning" role="alert">
                {t('onboarding.terms.versionChanged')}
              </div>
            )}
            <Card className="bg-light mb-4">
              <div className="position-relative">
                <div
                  ref={termsScrollRef}
                  onScroll={evaluateTermsScroll}
                  data-testid="terms-scroll"
                  className="onboarding-terms-scroll"
                  tabIndex={0}
                  aria-label={t('onboarding.terms.title')}
                >
                  {agreementLoading ? (
                    <div className="d-flex align-items-center gap-2 text-muted" role="status">
                      <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                      {t('common.loading')}
                    </div>
                  ) : agreementError ? (
                    <div className="alert alert-danger mb-0" role="alert">
                      {t('onboarding.terms.loadError')}
                    </div>
                  ) : (
                    <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: agreementContent }} />
                  )}
                </div>
                {!agreementLoading && !agreementError && !termsScrolledToEnd && (
                  <div
                    aria-hidden="true"
                    className="onboarding-terms-fade"
                  />
                )}
              </div>
            </Card>
            {!agreementLoading && !agreementError && agreementMeta.version && agreementMeta.savedAt && (
              <div className="d-flex flex-wrap justify-content-between gap-2 mb-3 small text-muted">
                <span>
                  {agreementMeta.title && <>{agreementMeta.title} · </>}
                  {t('onboarding.terms.version')} {agreementMeta.version} · {t('onboarding.terms.updated')}{' '}
                  {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(agreementMeta.savedAt))}
                </span>
                <Link
                  to={`/anvandarvillkor?version=${encodeURIComponent(agreementMeta.version)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('onboarding.terms.openPublic')}
                </Link>
              </div>
            )}
            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                className="custom-control-input"
                id="acceptTerms"
                checked={termsAccepted}
                disabled={!termsScrolledToEnd || agreementLoading || Boolean(agreementError)}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <label
                className={`custom-control-label ${termsScrolledToEnd ? 'c-pointer' : 'text-muted'}`}
                htmlFor="acceptTerms"
              >
                {t('onboarding.terms.accept')}
              </label>
            </div>
            <Link to="/integritet" target="_blank" rel="noopener noreferrer" className="d-inline-block py-3">{t('auth.bankid.privacy')}</Link>
            {termsScrolledToEnd ? (
              <div className="form-text text-muted mt-1">
                {t('onboarding.terms.scrolled', 'Du har läst igenom hela villkorstexten.')}
              </div>
            ) : (
              <div className="form-text text-warning mt-1">
                <i className="feather-arrow-down me-1"></i>
                {t('onboarding.terms.scrollHelp', 'Läs igenom användarvillkoren och scrolla till slutet av texten för att kunna godkänna dem.')}
              </div>
            )}
          </StepContent>
        );

      case 'bankid':
        return <div className="text-center"><p>{t('auth.bankid.loginDescription')}</p><Button onClick={() => { logout(); navigate('/login'); }}>{t('auth.bankid.loginAgain')}</Button></div>;

      case 'email':
        return <EmailConfirmation onEdit={() => setCurrentStepKey('profile')} onComplete={emailComplete} onResume={resumeStep} />;

      case 'complete':
        return (
          <div className="text-center py-5">
            <img
              src="/assets/images/general/completed-steps.png"
              alt=""
              className="img-fluid mb-4"
              width="240"
            />
            <h3 className="fw-bold">{t('onboarding.complete.title')}</h3>
            {verifiedFullName && (
              <div className="rounded border bg-light px-3 py-2 mx-auto mb-4 text-start">
                <div className="text-muted small">{t('onboarding.bankid.verifiedNameLabel')}</div>
                <div className="fw-semibold">{verifiedFullName}</div>
              </div>
            )}
            <p className="text-muted mb-3">{t('onboarding.complete.message')}</p>
            {isCompanyAccount && <div className="mb-4"><PingOnboardingPanel /></div>}
            {/* Center the primary action: full width on mobile (d-grid), auto
                width and horizontally centered from sm and up. The theme sets
                .btn to display:flex (block-level), so text-center alone would
                not center it. Shared for every persona's onboarding. */}
            <div className="d-grid d-sm-flex justify-content-sm-center">
              <Button className="bankid-action" onClick={handleComplete} isLoading={isLoading} size="lg">
                {t('onboarding.complete.toDashboard')}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStepKey) {
      case 'profile':
        if (!profileData.fullName || !profileData.phone || !profileData.email) return false;
        if (profileEmailProblem) return false;
        if (testserver && !/\.invalid$/i.test(profileData.email.trim())) return false;
        if (!normalizePhoneNumber(profileData.phone)) return false;
        if (profileData.postalCode && !/^\d{5}$/.test(profileData.postalCode)) return false;
        if (isPerson && !profileData.bankIds?.length) return false;
        if (isProviderAccount && !providerProfilePending) {
          if (!providerData.companyName || !providerData.organizationNumber) return false;
          if (!isValidOrgNumber(providerData.organizationNumber)) return false;
          if (providerOrgTaken) return false;
          return (
            providerData.notVatRegistered || isValidVatSE(providerData.vatNumber)
          );
        }
        return true;
      case 'company':
        if (!companyLoaded || companyLoadError) return false;
        if (
          !companyData.companyName ||
          !companyData.organizationNumber ||
          !companyData.vatNumber ||
          !companyData.address ||
          !companyData.city ||
          !companyData.postalCode
        ) {
          return false;
        }
        if (!/^\d{5}$/.test(companyData.postalCode)) return false;
        if (!isValidVatSE(companyData.vatNumber)) return false;
        return isValidOrgNumber(companyData.organizationNumber) && !companyOrgTaken;
      case 'terms':
        return Boolean(
          termsAccepted &&
          agreementContent &&
          agreementMeta.version &&
          agreementMeta.savedAt &&
          agreementMeta.agreementType &&
          !agreementLoading &&
          !agreementError
        );
      case 'bankid':
        return false;
      default:
        return true;
    }
  };

  return (
    <BankIdLayout wide={isCompanyAccount || isProviderAccount} below={<Button variant="light-brand" onClick={() => { logout(); navigate('/login'); }}>{t('nav.logout')}</Button>}>
      <RegistrationSteps companyAfterEmail={companyPreparedByAdmin} currentStep={companyPreparedByAdmin ? currentStep : currentStepKey === 'company' ? 1 : ['bankid', 'profile', 'terms', 'email', 'complete'].indexOf(currentStepKey)} />
      {saveError && <p className="alert alert-danger" role="alert">{saveError}</p>}
      {renderStepContent()}
      {!['complete', 'bankid', 'email'].includes(currentStepKey) && <div className="d-grid gap-2 d-sm-flex justify-content-sm-between mt-4 pt-4 border-top">
        <Button variant="light-brand" onClick={handlePrevious} disabled={currentStep <= 1}>{t('common.previous')}</Button>
        <Button className="bankid-action" onClick={handleNext} isLoading={isLoading} disabled={!canProceed()}>{t(currentStepKey === 'terms' ? 'auth.bankid.acceptContinue' : 'common.next')}</Button>
      </div>}
    </BankIdLayout>
  );
}
